// Admin API module — mirrors docs/API.md §8 (/api/v1/admin/*). Every call needs
// an admin session; the server enforces authz (403 `forbidden` otherwise) and
// audits every mutation. One-time license model: no expiry/plan/renewal params.

import { apiGet, apiPatch, apiPost } from "@/lib/api";
import type {
  AdminAuditEntry,
  AdminCommission,
  AdminCoupon,
  AdminCouponInput,
  AdminLicense,
  AdminMetrics,
  AdminOrder,
  AdminOrderDetail,
  AdminOrderStatus,
  AdminPricingTier,
  AdminUser,
  AdminUserDetail,
  AdminWebhookEvent,
  LicenseState,
  Paged,
  PaidTierId,
  ReferralCommissionStatus,
  Role,
  TierId,
} from "@/lib/types";

/** Common list paging. */
export interface PageQuery {
  limit?: number;
  offset?: number;
}

function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

export function getAdminMetrics(signal?: AbortSignal): Promise<AdminMetrics> {
  return apiGet<AdminMetrics>("/admin/metrics", { signal });
}

export function listAdminUsers(
  params: PageQuery & { q?: string },
  signal?: AbortSignal,
): Promise<Paged<AdminUser>> {
  return apiGet<Paged<AdminUser>>(
    `/admin/users${query({ q: params.q, limit: params.limit, offset: params.offset })}`,
    { signal },
  );
}

export function getAdminUser(id: string, signal?: AbortSignal): Promise<AdminUserDetail> {
  return apiGet<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`, { signal });
}

export function setAdminUserRole(id: string, role: Role): Promise<AdminUser> {
  return apiPatch<AdminUser>(`/admin/users/${encodeURIComponent(id)}/role`, { role });
}

export function grantAdminLicense(userId: string, tier: PaidTierId): Promise<AdminLicense> {
  return apiPost<AdminLicense>(`/admin/users/${encodeURIComponent(userId)}/license`, { tier });
}

/** UU PDP erasure — anonymize the user's PII (email/name/Google identity blanked)
 *  and revoke every session; orders + licenses are KEPT for bookkeeping. Audited
 *  server-side. Irreversible. */
export function eraseAdminUser(id: string, reason?: string): Promise<{ status: "erased" }> {
  return apiPost<{ status: "erased" }>(`/admin/users/${encodeURIComponent(id)}/erase`, {
    reason: reason?.trim() || undefined,
  });
}

export function listAdminOrders(
  params: PageQuery & { status?: AdminOrderStatus | ""; tier?: PaidTierId | "" },
  signal?: AbortSignal,
): Promise<Paged<AdminOrder>> {
  return apiGet<Paged<AdminOrder>>(
    `/admin/orders${query({
      status: params.status,
      tier: params.tier,
      limit: params.limit,
      offset: params.offset,
    })}`,
    { signal },
  );
}

export function getAdminOrder(id: string, signal?: AbortSignal): Promise<AdminOrderDetail> {
  return apiGet<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(id)}`, { signal });
}

/** Mark a PAID order refunded and revoke the license it granted (server enforces
 *  the status=paid precondition — 409 otherwise). The money itself moves manually
 *  at Pakasir; this records the outcome. */
export function refundAdminOrder(id: string, reason?: string): Promise<AdminOrder> {
  return apiPost<AdminOrder>(`/admin/orders/${encodeURIComponent(id)}/refund`, {
    reason: reason?.trim() || undefined,
  });
}

export function listAdminLicenses(
  params: PageQuery & { state?: LicenseState | ""; tier?: TierId | "" },
  signal?: AbortSignal,
): Promise<Paged<AdminLicense>> {
  return apiGet<Paged<AdminLicense>>(
    `/admin/licenses${query({
      state: params.state,
      tier: params.tier,
      limit: params.limit,
      offset: params.offset,
    })}`,
    { signal },
  );
}

export function revokeAdminLicense(id: string, reason?: string): Promise<AdminLicense> {
  return apiPost<AdminLicense>(`/admin/licenses/${encodeURIComponent(id)}/revoke`, {
    reason: reason ?? "",
  });
}

export function unbindAdminLicense(id: string, reason?: string): Promise<AdminLicense> {
  return apiPost<AdminLicense>(`/admin/licenses/${encodeURIComponent(id)}/unbind`, {
    reason: reason ?? "",
  });
}

export function listAdminWebhookEvents(
  params: PageQuery & { status?: string },
  signal?: AbortSignal,
): Promise<Paged<AdminWebhookEvent>> {
  return apiGet<Paged<AdminWebhookEvent>>(
    `/admin/webhook-events${query({
      status: params.status,
      limit: params.limit,
      offset: params.offset,
    })}`,
    { signal },
  );
}

export function listAdminCoupons(
  params: PageQuery,
  signal?: AbortSignal,
): Promise<Paged<AdminCoupon>> {
  return apiGet<Paged<AdminCoupon>>(
    `/admin/coupons${query({ limit: params.limit, offset: params.offset })}`,
    { signal },
  );
}

export function createAdminCoupon(input: AdminCouponInput): Promise<AdminCoupon> {
  return apiPost<AdminCoupon>("/admin/coupons", input);
}

export function updateAdminCoupon(id: string, input: AdminCouponInput): Promise<AdminCoupon> {
  return apiPatch<AdminCoupon>(`/admin/coupons/${encodeURIComponent(id)}`, input);
}

export function listAdminPricing(signal?: AbortSignal): Promise<AdminPricingTier[]> {
  return apiGet<AdminPricingTier[]>("/admin/pricing", { signal });
}

/** PATCH harga satu tier (rupiah utuh). Server menolak: free (terkunci), harga
 *  < Rp10.000 (floor kupon) atau > Rp100 jt (rem typo). */
export function updateAdminPricingTier(id: string, price: number): Promise<AdminPricingTier> {
  return apiPatch<AdminPricingTier>(`/admin/pricing/${encodeURIComponent(id)}`, { price });
}

export function listAdminAuditLog(
  params: PageQuery,
  signal?: AbortSignal,
): Promise<Paged<AdminAuditEntry>> {
  return apiGet<Paged<AdminAuditEntry>>(
    `/admin/audit-log${query({ limit: params.limit, offset: params.offset })}`,
    { signal },
  );
}

export function listAdminCommissions(
  params: PageQuery & { status?: ReferralCommissionStatus | "" },
  signal?: AbortSignal,
): Promise<Paged<AdminCommission>> {
  return apiGet<Paged<AdminCommission>>(
    `/admin/commissions${query({
      status: params.status,
      limit: params.limit,
      offset: params.offset,
    })}`,
    { signal },
  );
}

export function markAdminCommissionPaid(id: string, note?: string): Promise<AdminCommission> {
  return apiPost<AdminCommission>(`/admin/commissions/${encodeURIComponent(id)}/paid`, {
    note: note?.trim() || undefined,
  });
}

export function voidAdminCommission(id: string, note?: string): Promise<AdminCommission> {
  return apiPost<AdminCommission>(`/admin/commissions/${encodeURIComponent(id)}/void`, {
    note: note?.trim() || undefined,
  });
}
